import React, { useState, useEffect, useRef, useCallback } from 'react';
// Assuming Clerk is used for user context
import { useUser } from '@clerk/clerk-react';
// Assuming React Router for navigation
import { useNavigate } from 'react-router-dom'; // Used in Sidebar and ManageAssistants placeholder
import axios from 'axios'; // For fetchAgents
// Simple Markdown rendering (install react-markdown: npm i react-markdown)
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'; // For code blocks (npm i react-syntax-highlighter)
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'; // Code block style

// Import Lucide Icons - Combined List (Updated for simplified UI)
import {
    // Sidebar Needs (Simplified):
    LayoutDashboard, // Back to Dashboard
    Mail, // User Email Icon
    // Top Bar / General Needs:
    Search, Bell, ChevronDown, // Top Bar / General
    Menu, // Services Menu Icon
    Globe, // Region Icon
    LifeBuoy, // Support/Help Icon
    // Playground Needs:
    FlaskConical, // Page Title Icon
    Loader2, // Loading State
    X, // Close Modal / Cancel (Potentially for future config modal)
    ShieldAlert, // Warning / Error Icon
    Send, // Send Prompt Icon
    CheckSquare, // Selected Checkbox
    Square, // Unselected Checkbox
    ClipboardCopy, // Copy Response
    Check, // Success Icon (e.g., for Copy)
    Bot, // Agent Icon
    // Placeholder / Retained (Potentially unused):
    Settings, Users, Database, Link, BookOpen, Cpu, Wrench, HelpCircle, Server, Activity, DollarSign, BarChart3, Trash2, PlusCircle, Eye
} from 'lucide-react';


// --- Animation Utility Hook (Unchanged) ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') { setIsVisible(true); return; }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); }
        }, options);
        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef && observer) observer.unobserve(currentRef); };
    }, [options]);
    const hookRef = (node) => { ref.current = node; };
    return [hookRef, isVisible];
};

// --- Styled Clickable Components (Unchanged) ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel} onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div role="button" tabIndex={disabled ? -1 : 0} onClick={!disabled ? onClick : undefined} onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</div>
);

// --- Sidebar Nav Item Component (Reused Structure) ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
     <a href={href} onClick={onClick} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative text-gray-400 hover:text-white hover:bg-gray-800/50`}>
         {/* Active state indicator removed */}
         <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 text-gray-500 group-hover:text-gray-300`} strokeWidth={1.5} />
         <span className="truncate">{text}</span>
     </a>
);

// --- Simplified Sidebar Component ---
const Sidebar = () => {
    const navigate = useNavigate(); // Use navigate for routing
    const { user, isLoaded } = useUser(); // Get user object and loading state

    const handleBackToDashboardClick = (e) => {
        e.preventDefault(); // Prevent default anchor tag behavior
        console.log("Navigating back to /echidna/efoundry/dashboard");
        navigate('/echidna/efoundry/dashboard'); // Navigate using React Router
    };

    // Use primary email address, provide loading/fallback state
    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';

    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            {/* Header */}
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                <div className="flex items-center">
                    <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                    <span className="text-base font-bold text-white">Echidna Foundry</span>
                </div>
            </div>

            {/* Simplified Navigation Area */}
            <div className="flex-grow py-2">
                <nav className="space-y-0.5 px-2 mt-3"> {/* Added mt-3 for spacing */}
                    <SidebarNavItem
                        key="back-dashboard"
                        icon={LayoutDashboard}
                        text="Back to Dashboard"
                        href="/echidna/efoundry/dashboard" // href for context/right-click
                        active={false} // This item is never 'active' within this view
                        onClick={handleBackToDashboardClick} // Use navigate handler
                    />
                </nav>
            </div>

            {/* Updated User Info Section */}
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                 <ClickableLink
                    href="#profile" // Placeholder href
                    title={userEmail} // Tooltip shows the email
                    className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group w-full" // Added w-full
                 >
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate font-medium">{userEmail}</span>
                 </ClickableLink>
            </div>
        </div>
    );
};


// --- Top Bar Component (Updated User Info Fallback) ---
const TopBar = ({ pageTitle }) => {
     const { user } = useUser();
     // Use email as fallback for initials and display name
     const userInitial = user?.firstName?.[0] || user?.username?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
     const userDisplayName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName[0] + '.' : ''}` : user?.username || user?.primaryEmailAddress?.emailAddress || 'Account';

    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
             <div className="flex items-center space-x-3"><ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu"><Menu className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv><span className="text-sm font-medium text-white">{pageTitle}</span></div>
             <div className="flex items-center space-x-3 md:space-x-4"><div className="relative hidden md:block"><Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/><input type="search" placeholder={`Search ${pageTitle}...`} className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" /></div><ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region"><Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/><span>us-east-1</span></ClickableDiv><ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><LifeBuoy className="w-5 h-5" strokeWidth={1.5}/></ClickableLink><ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><Bell className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv>
             <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                 <span className="hidden md:inline mr-1 truncate max-w-[100px]" title={userDisplayName}>{userDisplayName}</span>
                 <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{userInitial.toUpperCase()}</span>
                 <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
             </ClickableDiv>
             </div>
         </div>
    );
};


// --- Agent Checkbox Component (Unchanged) ---
const AgentCheckbox = ({ agent, isSelected, onToggle, disabled }) => (
    <ClickableDiv
        onClick={() => onToggle(agent._id)}
        disabled={disabled}
        className={`flex items-center space-x-2 p-2 border rounded-md transition-colors duration-150 ${
            isSelected
                ? 'border-purple-500 bg-purple-900/20 text-purple-300'
                : 'border-gray-700 text-gray-400 hover:border-gray-600 hover:bg-gray-800/50'
        } ${disabled ? 'opacity-50 cursor-not-allowed hover:bg-transparent hover:border-gray-700' : 'cursor-pointer'}`}
        title={disabled ? 'Cannot change selection while running' : `Select ${agent.agentName}`}
    >
        {isSelected ? <CheckSquare className="w-4 h-4 text-purple-400 flex-shrink-0" /> : <Square className="w-4 h-4 text-gray-600 flex-shrink-0" />}
        <span className="text-xs font-medium truncate">{agent.agentName}</span>
    </ClickableDiv>
);

// --- Response Column Component (Unchanged) ---
const ResponseColumn = ({ agent, responseData }) => {
    const { response, status, error } = responseData || { status: 'idle' };
    const [copied, setCopied] = useState(false);

    const handleCopy = (textToCopy) => {
        if (!textToCopy) return;
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
        });
    };

    return (
        <div className="bg-gray-950 rounded-lg border border-gray-800/50 flex flex-col h-full shadow-sm">
            {/* Header */}
            <div className="flex justify-between items-center p-3 border-b border-gray-800/50 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                     <Bot className="w-4 h-4 text-purple-400 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm font-semibold text-gray-100 truncate" title={agent?.agentName}>{agent?.agentName || 'Agent'}</span>
                </div>
                 {(status === 'success' && response) && (
                    <ClickableDiv onClick={() => handleCopy(response)} title={copied ? "Copied!" : "Copy Response"} className="p-1 -m-1 text-gray-500 hover:text-purple-400 hover:bg-gray-800/50 rounded">
                        {copied ? <Check className="w-3.5 h-3.5 text-green-500" strokeWidth={2} /> : <ClipboardCopy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    </ClickableDiv>
                 )}
            </div>
            {/* Body */}
            <div className="p-4 flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-gray-950 min-h-[200px]">
                {status === 'loading' && ( <div className="flex items-center justify-center h-full text-gray-500"><Loader2 className="w-6 h-6 animate-spin" /></div> )}
                {status === 'error' && ( <div className="text-center text-red-400 text-xs p-4 bg-red-900/20 border border-red-700/30 rounded"><ShieldAlert className="w-5 h-5 mx-auto mb-1" />Error: {error || 'Failed to get response'}</div> )}
                 {status === 'success' && (
                    <div className="prose prose-sm prose-invert max-w-none text-gray-300 text-xs leading-relaxed">
                         <ReactMarkdown
                             components={{
                                 code({ node, inline, className, children, ...props }) {
                                     const match = /language-(\w+)/.exec(className || '');
                                     return !inline && match ? (
                                         <SyntaxHighlighter style={vscDarkPlus} PreTag="div" language={match[1]} {...props}>
                                             {String(children).replace(/\n$/, '')}
                                         </SyntaxHighlighter>
                                     ) : ( <code className="bg-gray-700/50 px-1 py-0.5 rounded text-xs" {...props}>{children}</code> );
                                 },
                                 p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                                 ul: ({ node, ...props }) => <ul className="list-disc list-outside mb-2 pl-4" {...props} />,
                                 ol: ({ node, ...props }) => <ol className="list-decimal list-outside mb-2 pl-4" {...props} />,
                                 li: ({ node, ...props }) => <li className="mb-0.5" {...props} />,
                                 a: ({ node, ...props }) => <a className="text-purple-400 hover:text-purple-300 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
                             }}
                         >{response || '*No response*'}</ReactMarkdown>
                     </div>
                )}
                {status !== 'loading' && status !== 'error' && status !== 'success' && ( <div className="text-center text-gray-600 text-xs italic pt-10">Response will appear here...</div> )}
            </div>
        </div>
    );
};


// --- Playground Content Component (API URLs Verified) ---
const PlaygroundContent = () => {
    const { user } = useUser();
    const cid = user?.id;

    // State
    const [agents, setAgents] = useState([]);
    const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
    const [prompt, setPrompt] = useState('');
    const [responses, setResponses] = useState({});
    const [isLoadingAgents, setIsLoadingAgents] = useState(true);
    const [isSendingPrompt, setIsSendingPrompt] = useState(false);
    const [fetchAgentsError, setFetchAgentsError] = useState(null);
    const [sendPromptError, setSendPromptError] = useState(null);
    const [lastRunAgents, setLastRunAgents] = useState([]);

    // Ref for animation
    const [checkboxesRef, areCheckboxesVisible] = useOnScreen({ threshold: 0.05 });

    // --- Fetch Agents (Using original Axios call) ---
    const fetchAgents = useCallback(async () => {
        if (!cid) {
            setIsLoadingAgents(false);
            setFetchAgentsError("User not authenticated.");
            return;
        }
        try {
            setIsLoadingAgents(true);
            setFetchAgentsError(null);
            // *** VERIFIED Axios Request URL ***
            const fetchUrl = 'https://web-production-5ee8.up.railway.app/echidna/myagents';
            console.log(`Playground: Fetching agents for CID: ${cid} from ${fetchUrl}`);
            const response = await axios.post(fetchUrl, { cid });
            console.log("Playground: Fetch agents response:", response.data);
            const fetchedAgents = response.data.agents || [];
            setAgents(fetchedAgents);

            if (fetchedAgents.length > 0 && selectedAgentIds.size === 0) {
                 setSelectedAgentIds(new Set([fetchedAgents[0]._id]));
                 console.log(`Playground: Pre-selected agent ${fetchedAgents[0]._id}`);
            } else if (fetchedAgents.length === 0) {
                setSelectedAgentIds(new Set());
            }
        } catch (err) {
            console.error("Playground: Fetch Agents Error:", err);
            const errorMsg = err.response?.data?.details || err.response?.data?.message || err.message || 'Failed to fetch agents';
            setFetchAgentsError(errorMsg);
            setAgents([]);
            setSelectedAgentIds(new Set());
        } finally {
            setIsLoadingAgents(false);
        }
    }, [cid, selectedAgentIds.size]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);


    // --- Toggle Agent Selection (Unchanged) ---
    const toggleAgentSelection = (agentId) => {
        if (isSendingPrompt) return;
        setSelectedAgentIds(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(agentId)) { newSelection.delete(agentId); }
            else { newSelection.add(agentId); }
            console.log("Playground: Updated selection:", Array.from(newSelection));
            return newSelection;
        });
    };

    // --- Send Prompt (Using original Fetch call) ---
    const handleSendPrompt = useCallback(async () => {
        if (selectedAgentIds.size === 0 || !prompt.trim()) {
            setSendPromptError("Please select at least one agent and enter a prompt.");
            return;
        }
        setIsSendingPrompt(true);
        setSendPromptError(null);
        const currentRunAgentIds = Array.from(selectedAgentIds);
        const currentRunAgentsData = agents.filter(a => currentRunAgentIds.includes(a._id));
        setLastRunAgents(currentRunAgentsData);

        const initialResponses = {};
        currentRunAgentIds.forEach(id => {
            initialResponses[id] = { response: '', status: 'loading', error: null };
        });
        setResponses(prev => ({ ...prev, ...initialResponses }));

        // Map over agent IDs to create an array of fetch promises
        const fetchPromises = currentRunAgentIds.map(async (agentId) => {
             // *** VERIFIED Fetch Request URL ***
            const apiUrl = `https://web-production-5ee8.up.railway.app/echidna/agents/v2/East-us/${agentId}`;
            console.log(`Playground: Sending prompt to agent ${agentId} via axios to ${apiUrl}`);
            let result = { agentId: agentId, status: 'rejected', reason: { message: 'Unknown request error' } }; // Default error structure

            try {
                const response = await axios.post(apiUrl, 
                    { message: prompt /*, cid: cid */ },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                console.log(`Playground: Raw response status for ${agentId}: ${response.status}`);
                console.log(`Playground: Success response data for ${agentId}:`, response.data);
                
                const responseText = (response.data && typeof response.data.message === 'string') 
                    ? response.data.message 
                    : 'Received success, but response format is unexpected.';
                    
                result = { agentId: agentId, status: 'fulfilled', value: { message: responseText } };
            } catch (err) {
                console.error(`Playground: Catch block error for ${agentId}:`, err);
                const errorData = err.response?.data;
                const errorMessage = errorData?.details || errorData?.message || err.message || 'Network error or failed request.';
                const errorStatus = err.response?.status || 'N/A';
                result.reason = { message: `${errorMessage} (Status: ${errorStatus})`, status: errorStatus };
            }
            return result;
        });


        // Wait for all fetch calls to settle
        const results = await Promise.all(fetchPromises);

        // Process results
        const finalResponses = {};
        results.forEach(result => {
             console.log(`Playground: Processing result for Agent ${result?.agentId}:`, result);
             if (!result || typeof result.agentId === 'undefined') {
                 console.error("Playground: Encountered a malformed result object:", result);
                 return;
             }
            const agentId = result.agentId;
            if (result.status === 'fulfilled') {
                const value = result.value;
                const responseText = (value && typeof value.message === 'string') ? value.message : 'Success, but no message found in response.';
                finalResponses[agentId] = { response: responseText, status: 'success', error: null };
                console.log(`Playground: Updated state for Success from agent ${agentId}`);
            } else {
                const reason = result.reason;
                const errorMessage = (reason && typeof reason.message === 'string') ? reason.message : 'Unknown error occurred.';
                finalResponses[agentId] = { response: '', status: 'error', error: `${errorMessage}` };
                console.error(`Playground: Updated state for Error from agent ${agentId}: ${errorMessage}`);
            }
        });

        // Update the main responses state
        setResponses(prev => ({ ...prev, ...finalResponses }));
        setIsSendingPrompt(false);

    }, [agents, cid, prompt, selectedAgentIds]); // Keep dependencies


    // --- Render Logic (Unchanged) ---
    return (
        // Uses padding from <main> container now
        <div className="flex flex-col h-full">
            {/* Header */}
            <header className="mb-8 flex-shrink-0">
                <div className="flex items-center gap-3 mb-1">
                    <FlaskConical className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                    <h1 className="text-3xl font-bold tracking-tight text-gray-100">Playground</h1>
                </div>
                <p className="text-base text-gray-400 sm:ml-10">Compare responses from multiple assistants side-by-side.</p>
            </header>

            {/* Agent Fetching Error Display */}
            {fetchAgentsError && ( <div className="mb-6 p-4 border border-red-700/50 bg-red-900/20 rounded-lg text-sm text-red-400"><ShieldAlert className="inline w-5 h-5 mr-2 -mt-1" />Error loading assistants: {fetchAgentsError}</div> )}
            {/* Prompt Sending Error Display */}
            {sendPromptError && ( <div className="mb-6 p-4 border border-yellow-700/50 bg-yellow-900/20 rounded-lg text-sm text-yellow-400">{sendPromptError}</div> )}

            {/* Configuration Section */}
            <div className="mb-6 bg-gray-950 border border-gray-800/50 rounded-lg p-4 flex-shrink-0">
                <h2 className="text-sm font-semibold text-gray-200 mb-3">Select Assistants to Compare</h2>
                {isLoadingAgents ? ( <div className="flex items-center justify-center p-4 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading Assistants...</div> )
                 : agents.length === 0 && !fetchAgentsError ? ( <p className="text-xs text-gray-500 italic">No assistants found for your account. Please create one first.</p> )
                 : (
                    <div ref={checkboxesRef} className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 transition-opacity duration-500 ${areCheckboxesVisible ? 'opacity-100' : 'opacity-0'}`}>
                        {agents.map(agent => ( <AgentCheckbox key={agent._id} agent={agent} isSelected={selectedAgentIds.has(agent._id)} onToggle={toggleAgentSelection} disabled={isSendingPrompt || isLoadingAgents}/> ))}
                    </div>
                 )}
            </div>

            {/* Response Area */}
             <div className={`flex-grow grid gap-6 ${ lastRunAgents.length > 1 ? `lg:grid-cols-${Math.min(lastRunAgents.length, 4)}` : '' } ${ lastRunAgents.length === 2 ? `md:grid-cols-2` : '' } grid-cols-1`}>
                {lastRunAgents.length > 0 ? (
                     lastRunAgents.map(agent => ( <ResponseColumn key={agent._id} agent={agent} responseData={responses[agent._id]}/> ))
                ) : ( !isLoadingAgents && ( <div className="col-span-full flex items-center justify-center text-gray-600 italic text-sm h-full min-h-[200px]">{agents.length > 0 ? "Select agents and send a prompt to see responses here." : "Create an assistant to get started."}</div> ) )}
            </div>


            {/* Input Area */}
            <div className="mt-6 pt-4 border-t border-gray-800/50 flex-shrink-0">
                <div className="relative">
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={`Ask a question to ${selectedAgentIds.size > 0 ? selectedAgentIds.size : 'selected'} assistant${selectedAgentIds.size !== 1 ? 's' : ''}... (Shift+Enter for new line)`}
                        className="w-full bg-gray-900 border border-gray-700 p-3 pr-28 text-sm text-white rounded-md focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 placeholder-gray-500 resize-none scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800"
                        rows={3}
                        disabled={isLoadingAgents}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!isSendingPrompt && selectedAgentIds.size > 0 && prompt.trim()) { handleSendPrompt(); } } }}
                    />
                    <ClickableDiv
                        onClick={handleSendPrompt}
                        disabled={isSendingPrompt || selectedAgentIds.size === 0 || !prompt.trim() || isLoadingAgents}
                        className={`absolute bottom-2.5 right-2.5 px-4 py-1.5 text-sm border rounded-md flex items-center gap-2 ${isSendingPrompt || selectedAgentIds.size === 0 || !prompt.trim() || isLoadingAgents ? 'border-gray-700 bg-gray-800 text-gray-500 cursor-not-allowed' : 'border-purple-700/50 bg-purple-600 hover:bg-purple-700 text-white cursor-pointer'}`}
                        title={isLoadingAgents ? "Loading agents..." : selectedAgentIds.size === 0 ? "Select an agent first" : !prompt.trim() ? "Enter a prompt" : "Send Prompt (Enter)"}
                    >
                        {isSendingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5}/>}
                        <span>{isSendingPrompt ? 'Sending...' : 'Send'}</span>
                    </ClickableDiv>
                </div>
            </div>
        </div>
    );
};

// --- Main Application Component (Eplayground - Simplified) ---
function Eplayground() {
    // Fixed page title
    const pageTitle = 'Playground';

    // Render content is always PlaygroundContent
    const renderContent = () => {
        return <PlaygroundContent />;
    };

  return (
    <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
        {/* Use simplified Sidebar */}
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* TopBar displays fixed title, uses updated user info */}
            <TopBar pageTitle={pageTitle} />
            {/* Main content area renders PlaygroundContent */}
            <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black p-6 lg:p-8">
                 <div className="relative z-10 h-full">
                    {renderContent()}
                 </div>
            </main>
        </div>
        {/* Global styles */}
        <style jsx global>{`
            /* Echtools global styles */
            .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
            .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
            .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
            .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
            body { background-color: black; margin: 0; }
            @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
            .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
            @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
            .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
            @keyframes spin { to { transform: rotate(360deg); } }
            .animate-spin { animation: spin 1s linear infinite; }
            .prose code::before, .prose code::after { content: ''; }
            .prose-invert { --tw-prose-body: theme(colors.gray.300); --tw-prose-headings: theme(colors.white); --tw-prose-lead: theme(colors.gray.400); --tw-prose-links: theme(colors.purple.400); --tw-prose-bold: theme(colors.white); --tw-prose-counters: theme(colors.gray.400); --tw-prose-bullets: theme(colors.gray.600); --tw-prose-hr: theme(colors.gray.700); --tw-prose-quotes: theme(colors.gray.100); --tw-prose-quote-borders: theme(colors.gray.700); --tw-prose-captions: theme(colors.gray.400); --tw-prose-code: theme(colors.white); --tw-prose-pre-code: theme(colors.gray.300); --tw-prose-pre-bg: theme(colors.gray.900); --tw-prose-th-borders: theme(colors.gray.600); --tw-prose-td-borders: theme(colors.gray.700); --tw-prose-invert-body: theme(colors.gray.300); --tw-prose-invert-headings: theme(colors.white); --tw-prose-invert-lead: theme(colors.gray.400); --tw-prose-invert-links: theme(colors.purple.400); --tw-prose-invert-bold: theme(colors.white); --tw-prose-invert-counters: theme(colors.gray.400); --tw-prose-invert-bullets: theme(colors.gray.600); --tw-prose-invert-hr: theme(colors.gray.700); --tw-prose-invert-quotes: theme(colors.gray.100); --tw-prose-invert-quote-borders: theme(colors.gray.700); --tw-prose-invert-captions: theme(colors.gray.400); --tw-prose-invert-code: theme(colors.white); --tw-prose-invert-pre-code: theme(colors.gray.300); --tw-prose-invert-pre-bg: rgb(31 41 55 / 50%); --tw-prose-invert-th-borders: theme(colors.gray.600); --tw-prose-invert-td-borders: theme(colors.gray.700); }
            .prose pre { background-color: transparent !important; margin: 0 !important; padding: 0 !important; } /* Remove extra padding/margin from highlighter */
        `}</style>
    </div>
  );
}

export default Eplayground;